import * as React from "react";

import { useWorkspaceTimer } from "../../../WorkspaceCtx/hooks";
import { PlotLoadingState } from "../messaging/events";
import { Extents } from "../PlotRenderer";

/**
 * Hold the timer while the plot is loading.
 */
export function useHoldWhileLoading(isPlotLoading: PlotLoadingState) {
  const timer = useWorkspaceTimer();
  const timerHoldRef = React.useRef<symbol | null>(null);
  React.useEffect(
    function holdTimerWhenLoading() {
      const loadingForeground = isPlotLoading === "foreground";
      if (loadingForeground && timerHoldRef.current === null) {
        timerHoldRef.current = timer.hold();
      }
      if (!loadingForeground && timerHoldRef.current !== null) {
        timer.holdRelease(timerHoldRef.current);
        timerHoldRef.current = null;
      }

      return function cleanUp() {
        if (timerHoldRef.current !== null) {
          timer.holdRelease(timerHoldRef.current);
          timerHoldRef.current = null;
        }
      };
    },
    [isPlotLoading, timer],
  );
}

/**
 * Seek Timer to a specific time when the user clicks on the plot.
 */
export function useSeekToClick(
  chartExtents: Extents | null,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  isDragging: boolean,
) {
  const timer = useWorkspaceTimer();
  const isDraggingRef = React.useRef(isDragging);
  isDraggingRef.current = isDragging;
  React.useEffect(
    function attachSeekToListener() {
      if (chartExtents === null || canvasRef.current === null) {
        return;
      }
      const canvas = canvasRef.current;
      if (canvas === null) {
        return;
      }

      const abortController = new AbortController();
      function seekToTime(event: MouseEvent) {
        if (chartExtents === null || canvas === null) {
          return;
        }

        // Don't seek while dragging
        if (isDraggingRef.current) {
          return;
        }

        const {
          pixel: { left: canvasScaleLeftOffset, width },
          data: {
            viewportLogTime: { min, max },
          },
        } = chartExtents;

        const canvasBox = canvas.getBoundingClientRect();

        const x = Math.max(
          Math.min(
            event.clientX - canvasScaleLeftOffset - canvasBox.left,
            width,
          ),
          0,
        );

        const pct = x / width;
        const nextTime = min + BigInt(Math.round(Number(max - min) * pct));

        timer.seekTo(nextTime);
      }
      canvas.addEventListener("click", seekToTime, {
        signal: abortController.signal,
      });

      return function detachSeekToListener() {
        abortController.abort();
      };
    },
    [chartExtents, canvasRef, timer],
  );
}
